/**
 * `RouteErrorBoundary` — route-level error boundary (Req 17.4).
 *
 * Purpose
 * -------
 * Catches rendering errors thrown by any descendant in a routed subtree
 * and swaps that subtree for an Error fallback UI, WITHOUT unmounting the
 * app-level chrome. Because `router/routes.tsx` wraps every route element
 * in this boundary (task 15.3), and `App.tsx` mounts `<Navbar />` and
 * `<Footer />` OUTSIDE the routed `<Outlet />` (task 15.4), a throw inside
 * a page component collapses to a per-route fallback while the navbar and
 * footer keep rendering — exactly the behaviour required by Requirement
 * 17.4:
 *
 *   "WHEN a component throws a rendering error, THE Frontend SHALL catch
 *    the error at a route-level error boundary and render the Error
 *    component in place of the failed subtree while continuing to render
 *    the Navbar and Footer."
 *
 * Why a class
 * -----------
 * React 18 still has no hooks-based equivalent of `getDerivedStateFromError`
 * / `componentDidCatch`. A class component is the only way to intercept
 * render-time throws from descendants, so this file is intentionally the
 * one small class-based island in an otherwise hooks-first frontend.
 *
 * Recovery
 * --------
 * `reset()` clears `state.error` and re-renders `children`. The optional
 * `fallback` render prop receives `reset` so callers can offer a
 * "try again" affordance (matching the retry semantics of Req 17.1 / 17.3
 * — retry control on the Error component).
 *
 * Follow-up (task 16.2)
 * ---------------------
 * The shared `<ErrorComponent onRetry={reset} />` does not exist yet — it
 * lands in task 16.2. To keep task 15.1 self-contained and avoid a forward
 * dependency that would block wiring `routes.tsx` (task 15.3), the default
 * fallback below is a minimal inline `role="alert"` block. Once
 * `ErrorComponent` exists, the default branch of `render()` can be
 * refactored to `return <ErrorComponent onRetry={this.reset} error={error} />;`
 * without changing this component's public API — `routes.tsx` needs no
 * edits and any caller-supplied `fallback` prop continues to take
 * precedence.
 *
 * Requirements satisfied
 * ----------------------
 *   - Req 17.4: routed subtree errors are caught here and replaced with an
 *     Error fallback UI, while Navbar and Footer (mounted outside the
 *     routed area in `App.tsx`) remain visible.
 */

import React, { type PropsWithChildren } from 'react';

/**
 * Props for `RouteErrorBoundary`.
 *
 * `children` is the routed subtree the boundary protects. `fallback` is an
 * optional render prop invoked when a descendant throws; it receives the
 * caught error and a `reset` callback that clears the boundary's error
 * state so the next render re-mounts `children`. Callers that omit
 * `fallback` get the minimal inline fallback described in the header
 * docblock (task 16.2 will replace that inline UI with `ErrorComponent`).
 */
type RouteErrorBoundaryProps = PropsWithChildren<{
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}>;

/**
 * Boundary state.
 *
 * `null` while the routed subtree renders normally, and the caught `Error`
 * once a descendant has thrown during render. Storing the error itself
 * (rather than a boolean flag) lets the fallback render error-specific
 * messaging and lets the future `ErrorComponent` (task 16.2) surface the
 * underlying failure to logs and telemetry.
 */
interface RouteErrorBoundaryState {
  error: Error | null;
}

/**
 * Route-level error boundary. See file header for rationale and lifecycle.
 */
export class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  public state: RouteErrorBoundaryState = { error: null };

  /**
   * React calls this on the render commit that follows a descendant throw.
   * Deriving state HERE (rather than only in `componentDidCatch`) is
   * required so the fallback UI renders on the same commit — otherwise the
   * user would briefly see nothing before the boundary switches over.
   */
  public static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  /**
   * Called after `getDerivedStateFromError` and after the fallback has
   * committed. This is the correct hook for side effects (logging).
   *
   * We gate the log to development because in production the visible
   * fallback IS the user-facing signal — writing the component stack to
   * `console.error` there would just add noise without a consumer. When
   * telemetry lands, this branch is the natural place to forward the
   * error, so it stays a discrete method rather than being inlined.
   */
  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error(
        '[RouteErrorBoundary] captured a route render error',
        error,
        errorInfo,
      );
    }
  }

  /**
   * Arrow-function class property so `this` is captured lexically. That
   * lets the boundary hand `reset` directly to the fallback (and, in
   * task 16.2, to `<ErrorComponent onRetry={this.reset} />`) without
   * per-call `.bind(this)` or wrapper closures at every call site.
   */
  private reset = (): void => {
    this.setState({ error: null });
  };

  public render(): React.ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error !== null) {
      if (fallback) {
        return fallback(error, this.reset);
      }

      // Minimal inline fallback. Replaced by `<ErrorComponent
      // onRetry={this.reset} />` in task 16.2; kept here now so this
      // boundary can be wired into `router/routes.tsx` (task 15.3)
      // without a forward dependency on task 16.2. `role="alert"` gives
      // assistive tech an immediate announcement that the route failed.
      return (
        <div role="alert" style={{ padding: '2rem' }}>
          <h1>Something went wrong on this page.</h1>
          <p>The site is temporarily unavailable. Please try again.</p>
          <button type="button" onClick={this.reset}>
            Try again
          </button>
        </div>
      );
    }

    return children;
  }
}
